/**
 * Indicadores de saúde com label — bateria e sinal com percentual + cor.
 *
 * Usado nas tabelas onde queremos mais informação que o ícone solto.
 *  - <BatteryIndicator pct={87} /> → 🔋 87%
 *  - <SignalIndicator dbm={-65} />  → 📶 -65 dBm (98%)
 *  - <SignalIndicator lqi={180} />  → 📶 LQI 180 (71%)
 */

import {
  Battery,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  BatteryWarning,
  Signal,
  SignalHigh,
  SignalLow,
  SignalMedium,
  SignalZero,
  WifiOff,
} from "lucide-react";
import clsx from "clsx";

export type Tone = "success" | "ok" | "warn" | "alert" | "off" | "unknown";

const TONE_CLASS: Record<Tone, string> = {
  success: "text-emerald-600",
  ok:      "text-slate-600 dark:text-slate-400",
  warn:    "text-amber-600",
  alert:   "text-red-600",
  off:     "text-slate-400 dark:text-slate-500",
  unknown: "text-slate-400 dark:text-slate-500",
};

// ---------------------------------------------------------------------------
// Bateria
// ---------------------------------------------------------------------------

export function batteryTone(pct: number | null | undefined): Tone {
  if (pct === null || pct === undefined) return "unknown";
  if (pct <= 10)  return "alert";
  if (pct <= 20)  return "warn";
  if (pct <= 50)  return "ok";
  return "success";
}

function batteryIcon(pct: number | null | undefined) {
  if (pct === null || pct === undefined) return Battery;
  if (pct <= 10)  return BatteryWarning;
  if (pct <= 20)  return BatteryLow;
  if (pct <= 50)  return BatteryMedium;
  return BatteryFull;
}

export function BatteryIndicator({
  pct,
  size = "md",
  className,
}: {
  pct: number | null | undefined;
  size?: "sm" | "md";
  className?: string;
}) {
  const Icon = batteryIcon(pct);
  const tone = batteryTone(pct);
  const label = pct === null || pct === undefined ? "—" : `${pct}%`;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 tabular-nums",
        TONE_CLASS[tone],
        size === "sm" ? "text-xs" : "text-sm",
        className,
      )}
    >
      <Icon className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sinal
// ---------------------------------------------------------------------------

/**
 * Converte dBm WiFi → qualidade percentual aproximada (linear -100..-30).
 * -50 dBm ~= 100%, -100 dBm ~= 0%.
 */
export function dbmToPercent(dbm: number): number {
  if (dbm >= -50) return 100;
  if (dbm <= -100) return 0;
  return Math.round(2 * (dbm + 100));
}

/**
 * Zigbee LQI 0-255 → percentual aproximado.
 */
export function lqiToPercent(lqi: number): number {
  return Math.max(0, Math.min(100, Math.round((lqi / 255) * 100)));
}

export function signalTone(
  kind: "wifi" | "zigbee",
  value: number | null | undefined,
): Tone {
  if (value === null || value === undefined) return "unknown";
  if (kind === "wifi") {
    if (value > -60) return "success";
    if (value > -75) return "ok";
    if (value > -85) return "warn";
    return "alert";
  }
  if (value > 200) return "success";
  if (value > 100) return "ok";
  if (value > 50)  return "warn";
  return "alert";
}

function signalIcon(kind: "wifi" | "zigbee", value: number | null | undefined) {
  const t = signalTone(kind, value);
  if (t === "alert") return SignalZero;
  if (t === "warn")  return SignalLow;
  if (t === "ok")    return SignalMedium;
  if (t === "success") return SignalHigh;
  return Signal;
}

interface SignalProps {
  /** dBm pra wifi, omita se for zigbee. */
  dbm?: number | null;
  /** LQI 0-255 pra zigbee. */
  lqi?: number | null;
  size?: "sm" | "md";
  /** Mostrar valor bruto + (% inferido). Default true. */
  showRaw?: boolean;
  className?: string;
}

export function SignalIndicator({
  dbm,
  lqi,
  size = "md",
  showRaw = true,
  className,
}: SignalProps) {
  const isWifi = dbm !== undefined && dbm !== null;
  const isZigbee = !isWifi && lqi !== undefined && lqi !== null;

  if (!isWifi && !isZigbee) {
    return (
      <span
        className={clsx(
          "inline-flex items-center gap-1.5",
          TONE_CLASS.unknown,
          size === "sm" ? "text-xs" : "text-sm",
          className,
        )}
      >
        <Signal className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
        —
      </span>
    );
  }

  const kind: "wifi" | "zigbee" = isWifi ? "wifi" : "zigbee";
  const value = isWifi ? dbm! : lqi!;
  const Icon = signalIcon(kind, value);
  const tone = signalTone(kind, value);
  const pct = isWifi ? dbmToPercent(value) : lqiToPercent(value);

  const label = showRaw
    ? isWifi
      ? `${value} dBm · ${pct}%`
      : `LQI ${value} · ${pct}%`
    : `${pct}%`;

  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 tabular-nums",
        TONE_CLASS[tone],
        size === "sm" ? "text-xs" : "text-sm",
        className,
      )}
    >
      <Icon className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Online / Offline
// ---------------------------------------------------------------------------

export function OnlineIndicator({ online }: { online: boolean | undefined }) {
  if (online === undefined) {
    return <span className="text-xs text-slate-400 dark:text-slate-500">sem status</span>;
  }
  if (!online) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-red-600">
        <WifiOff className="h-3.5 w-3.5" />
        offline
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      online
    </span>
  );
}
